import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Observable, Subscription, firstValueFrom } from "rxjs";
import { ProfileData } from "src/app/core/state/profile/profile.state";
import { SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { CryptoService } from "src/app/core/firebase/crypto.service";
import { CryptoIsLoggedInType, CryptoStateDispatcher } from "src/app/core/state/crypto/crypto.dispatcher";
import { SOPO_DIALOG_DATA, SopoDialogRef } from "src/app/sopo-popup/interfaces/dialog";
import { BetButtonsComponent } from "../../the-rail/profile-game-settings/bet-buttons/bet-buttons.component";
import { SopoAlertSettings } from "src/app/sopo-popup/interfaces/alert";
import { WalletHandlerService } from "src/app/core/wallets/walletHandler.service";
import { FeatureService } from "src/app/core/firebase/feature.service";
import { NavController } from "@ionic/angular";
import { AuthStoreDispatcher } from "src/app/core/state/auth/auth.dispatcher";
import { VerificationService } from "src/app/verification-page/verification.service";
import { AuthService } from "src/app/core/firebase/auth.service";
import { formatDate } from "@angular/common";
import { ILoginAndCreationTimes } from "../../../profile-page.service";

const FIELD_IDS = [
    'userId',
    'signupMethod',
    'loginEmail',
    'contactEmail',
    'joinDate',
    'lastLoginDate'
] as const;

type FieldIdsType = typeof FIELD_IDS[number];

@Component({
    selector: 'more-information',
    templateUrl: './more-information.component.html',
    styleUrls: ['./more-information.component.scss'],
    standalone: false
})
export class MoreInformationComponet implements OnInit, OnDestroy {
    public isCryptoLoggedIn$: Observable<CryptoIsLoggedInType>;
    public phantomEnabled = true;
    public fields: IField[] = [];
    
    private _providerId: string;
    private _authProviderId = AuthProviderIds;
    private _phantomEnabledSubscription: Subscription;

    constructor(
        private sopoPopupService: SopoPopupService,
        private cryptoService: CryptoService,
        private cryptoDispatcher: CryptoStateDispatcher,
        private sopoDialogRef: SopoDialogRef,
        @Inject(SOPO_DIALOG_DATA) public data: {
            profileData: ProfileData;
            loginAndCreationTimes: ILoginAndCreationTimes;
        },
        private walletHandlerService: WalletHandlerService,
        private featureService: FeatureService,
        private ref: ChangeDetectorRef,
        private navCtrl: NavController,
        private authService: AuthService,
        private authStoreDispatcher: AuthStoreDispatcher,
        private verificationService: VerificationService,
    ) {}

    ngOnInit(): void {
        this.initialize();
        this.initializeSubscriptions();
        this.isCryptoLoggedIn$ =  this.cryptoDispatcher.isLoggedIn();
    }

    private async initialize(): Promise<void> {
        this._providerId = this.authService.getUserProviderId();
        const emailVerified = await firstValueFrom(this.verificationService.isEmailVerified());

        let contactEmail = '';
        if (emailVerified) contactEmail = await this.authStoreDispatcher.getAuthEmail();

        const fieldDataMap: Record<string, IFieldData> = {
            userId: { name: 'User ID', value: this.data.profileData.shortID },
            signupMethod: { name: 'Sign up Method', value: this._providerId },
            loginEmail: { name: 'Login Email', value: this.data.profileData.email },
            contactEmail: { name: 'Contact Email', value: contactEmail },
            joinDate: { name: 'Date Joined', value: this.data.loginAndCreationTimes.creationTime },
            lastLoginDate: { name: 'Last Login', value: this.data.loginAndCreationTimes.lastSignInTime },
        };

        for (const id of FIELD_IDS) {
            const fieldData = fieldDataMap[id];
            if (fieldData && fieldData.value) this.processField(id, fieldData);
        }
    }
    
    private initializeSubscriptions(): void {
        this._phantomEnabledSubscription = this.featureService.phantomEnabled$.subscribe(status => {
            this.phantomEnabled = status;
            this.ref.detectChanges();
        });
    }

    private processField(id: FieldIdsType, fieldData: IFieldData) {
        if (id === 'loginEmail' && this._providerId !== this._authProviderId.Password ) return;

        let value = fieldData.value;
        if (id === 'signupMethod') value = this.getSignupMethod();
        else if (id === 'joinDate' || id === 'lastLoginDate') value = formatDate(new Date(fieldData.value), 'd MMM yyyy', 'en-US');
        
        this.fields.push({
            id: id,
            name: fieldData.name,
            value,
        });
    }

    public async connectCryptoWallet(event: any, type: 'metamask' | 'phantom'): Promise<void> {
        const isCryptoLoggedIn = await firstValueFrom(this.isCryptoLoggedIn$);

        if (!isCryptoLoggedIn) {
            if (type === 'metamask') this.cryptoService.connectToMetamask('app');
            else this.walletHandlerService.connectPhantom();
            return;
        }

        if (isCryptoLoggedIn === 'evm' && type === 'phantom' ||
            isCryptoLoggedIn === 'solana' && type === 'metamask'
        ) {
            this.showAlert({
                message: 'Please disconnect from your current wallet before switching.',
            }, 2000);
            return;
        }

        if (isCryptoLoggedIn) {
            this.sopoPopupService.promptForConfirmation('app', {
                data: {
                    header: 'Are You Sure You Want To Disconnect?',
                    okText: 'Yes',
                    cancelText: 'No',
                }
            })
            .then((logout) => {
                if (logout) {
                    event.stopPropagation();
                    if (type === 'metamask') this.cryptoService.disconnectFromMetamask();
                    else this.walletHandlerService.disconnectPhantom();
                }
            });
        }
    }

    async showBets() {
        this.sopoPopupService.openDialog('app', BetButtonsComponent, {
        });
    }

    close() {
        this.sopoDialogRef.close();
    }

    private showAlert(sopoAlertSettings: SopoAlertSettings, closeAfter?: number): void {
        if (closeAfter && closeAfter !== 0) sopoAlertSettings.dismissAfter = closeAfter;
        this.sopoPopupService.clearViewContainer('app');
        this.sopoPopupService.showAlert('app', sopoAlertSettings);
    }

    canChangePassword(): boolean {
        return this._providerId === this._authProviderId.Password;
    }
    
    changePassword() {
        this.navCtrl.navigateForward("update-password", {
            queryParams: {
                fromProfile: "true"
            }
        });
    }

    public getSignupMethod(): string {
        switch (this._providerId) {
            case this._authProviderId.Apple:
                return 'Apple';
            case this._authProviderId.Facebook:
                return 'Facebook';
            case this._authProviderId.Google:
                return 'Google';
            case this._authProviderId.Password:
                return 'Email/Password';
            default:
                return '';
        }
    }

    ngOnDestroy(): void {
        if (this._phantomEnabledSubscription) this._phantomEnabledSubscription.unsubscribe();
    }
}

interface IField {
    id: FieldIdsType;
    name: string;
    value: string | number;
}

interface IFieldData {
    name: string;
    value: string | number;
};

enum AuthProviderIds {
  Apple = 'apple.com',
  Facebook = 'facebook.com',
  Google = 'google.com',
  Password = 'password',
}
